import dayjs from 'dayjs'
import { Dictionary, NlpModelId, QueryListFilter } from '@feedyou-ai/feedbot-schema'

import { useRateQuery } from './useRateQuery'
import { ColumnName, TableColumn } from './types'
import { parseQueryOutputToolCalls } from '../utils'
import { useUserRole } from '../../../store/user/hooks/useUserRole'
import { useDateTimePresetOption } from '../components/Filters/DateTimeOptions/hooks'
import { getDefaultDateFilterTimestamps, Preset } from '../components/Filters/DateTimeOptions/utils'
import { filterDictionaryByConditionField } from '../../../lib/object/filterDictionaryByConditionField'
import {
  RatingOptions,
  RatingControls,
  ControlButtons,
  DateTimeOptions,
  QueryMenuSettings
} from '../components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'

type Params = {
  modelId?: NlpModelId
  clearPageHistory?: () => void
  activeFilter: {
    filter: QueryListFilter
    setFilter(filter: QueryListFilter): void
  }
  uncommitedFilter: {
    filter: QueryListFilter
    setFilterValue<K extends keyof QueryListFilter>(key: K, val: QueryListFilter[K]): void
  }
}

export function useBaseQueryTableColumns(params: Params) {
  return Object.values(useBaseQueryTableColumnsByColumnName(params))
}

export function useBaseQueryTableColumnsByColumnName(
  params: Params
): Dictionary<ColumnName, TableColumn> {
  const role = useUserRole()
  const rateQuery = useRateQuery()
  const dateTimePresetOption = useDateTimePresetOption(params.activeFilter.filter)

  const isAdmin = role === 'admin'
  const isCustomer = ['comment', 'copywrite', 'edit'].includes(role)

  return filterDictionaryByConditionField({
    Timestamp: {
      name: 'Timestamp',
      header: 'Date & Time',
      controls: (
        <DateTimeOptions
          presetOption={dateTimePresetOption}
          activeFilter={params.activeFilter.filter}
          uncommitedFilter={params.uncommitedFilter.filter}
          setTimestampFrom={timestamp =>
            params.uncommitedFilter.setFilterValue('fromTimestamp', timestamp)
          }
          setTimestampTo={timestamp =>
            params.uncommitedFilter.setFilterValue('toTimestamp', timestamp)
          }
        />
      ),
      createCellProps: query => ({
        style: { whiteSpace: 'nowrap' },
        title: `User ID: ${query.userId}\nQuery ID: ${query.queryId}`,
        value: dayjs(query.timestamp).format('D/M/YYYY HH:mm')
      })
    },
    UserId: {
      name: 'UserId',
      header: 'User ID',
      controls: (
        <input
          type="text"
          id="UserId"
          name="UserId"
          value={params.uncommitedFilter.filter.userId || ''}
          onChange={e => params.uncommitedFilter.setFilterValue('userId', e.target.value)}
        />
      ),
      createCellProps: query => ({ value: query.userId })
    },
    SearchFilter: {
      name: 'SearchFilter',
      header: 'Search Filter',
      controls: (
        <input
          type="text"
          id="SearchFilter"
          name="SearchFilter"
          value={params.uncommitedFilter.filter.searchFilter || ''}
          onChange={e => params.uncommitedFilter.setFilterValue('searchFilter', e.target.value)}
        />
      ),
      createCellProps: query => ({ value: query.searchFilter || '' })
    },
    Input: {
      name: 'Input',
      header: 'Input',
      controls: null,
      createCellProps: query => ({
        value: typeof query.input === 'string' ? query.input : '-'
      })
    },
    Output: {
      name: 'Output',
      header: 'Output',
      controls: null,
      createCellProps: query => {
        const toolCalls = parseQueryOutputToolCalls(query.toolCalls)

        return {
          value: (
            <>
              {query.output}
              <ToolCalls>
                {toolCalls.map(({ tool, args }) => {
                  return <ToolCall tool={tool} args={args} />
                })}
              </ToolCalls>
            </>
          ),

          style: {
            verticalAlign: 'baseline',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }
        }
      }
    },
    UserRating: {
      name: 'UserRating',
      header: 'User rating',
      controls: <RatingOptions name="UserRating" uncommitedFilter={params.uncommitedFilter} />,
      createCellProps: query => ({
        title: query.userRatingExplanation,
        value: (
          <RatingControls
            disableRating
            explanation={query.userRatingExplanation || ''}
            descriptor={{ day: query.day, queryId: query.queryId }}
            rating={(query.hasOwnProperty('rating') ? query.rating : query.userRating) || 0}
          />
        )
      })
    },
    CustomerRating: {
      name: 'CustomerRating',
      header: 'Customer rating',
      controls: <RatingOptions name="CustomerRating" uncommitedFilter={params.uncommitedFilter} />,
      createCellProps: query => ({
        title: 'Customer rating',
        value: (
          <RatingControls
            rating={query.customerRating || 0}
            disableRating={!isCustomer || isAdmin}
            explanation={query.customerRatingExplanation || ''}
            descriptor={{ day: query.day, queryId: query.queryId }}
          />
        )
      })
    },
    AdminRating: {
      condition: isAdmin,
      name: 'AdminRating',
      header: 'Admin Rating',
      controls: <RatingOptions name="AdminRating" uncommitedFilter={params.uncommitedFilter} />,
      createCellProps: query => ({
        title: query.adminRatingExplanation,
        value: (
          <RatingControls
            disableRating={!isAdmin}
            descriptor={{ day: query.day, queryId: query.queryId }}
            rating={
              (query.hasOwnProperty('internalRating') ? query.internalRating : query.adminRating) ||
              0
            }
            explanation={
              (query.hasOwnProperty('explanation')
                ? query.explanation
                : query.adminRatingExplanation) || ''
            }
          />
        )
      })
    },
    Actions: {
      name: 'Actions',
      header: 'Actions',
      controls: (
        <ControlButtons
          resetActiveFilter={() => {
            params.clearPageHistory?.()
            dateTimePresetOption.setValue(undefined)
            params.activeFilter.setFilter(getDefaultDateFilterTimestamps())
          }}
          commitCurrentFilterAsActive={() =>
            params.activeFilter.setFilter(params.uncommitedFilter.filter)
          }
        />
      ),
      createCellProps: query => ({
        value: (
          <QueryMenuSettings
            queryId={query.queryId}
            modelId={query.modelId}
            userId={query.userId}
            context={query.context}
            commitUserFilter={() => {
              params.clearPageHistory?.()
              const activeFilter = params.activeFilter.filter
              params.activeFilter.setFilter({ ...activeFilter, userId: query.userId })
            }}
          />
        )
      })
    }
  } as const)
}

const ToolCalls = styled.span`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const ToolCall = ({ tool, args = '' }: { tool: string; args?: string }) => {
  return (
    <span>
      <FontAwesomeIcon
        color="rgb(96, 123, 130)"
        style={{ height: '0.8em' }}
        icon={'screwdriver-wrench'}
      />
      <span style={{ paddingLeft: 4, fontFamily: 'Courier New, monospace' }}>
        {tool} {args}
      </span>
    </span>
  )
}
